"use client"

import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import DashNavbar from "./DashNavbar"
import Sidebar from "./Sidebar"
import "../css/dash.css"
import "bootstrap/dist/css/bootstrap.min.css"
import "../css/PDFViewer.css"
import Swal from "sweetalert2"
import { Carousel, Spinner } from "react-bootstrap"

// Import the video files
import WhyVetrinaVideo from "../../Assets/Why Vetrina_English.mp4"
import VetZoneVideo from "../../Assets/vetzone.mp4"

// Import the images for Vision and Mission
import VisionImage from "../../Assets/vision.png"
import MissionImage from "../../Assets/mision.png"

// Import the images for Employee Benefits
import ServiceIcon from "../../Assets/Service.png"
import ProductIcon from "../../Assets/product.png"
import CustomerIcon from "../../Assets/customer.png"
import PeopleIcon from "../../Assets/People.png"

// Import the slider images from the Assets folder
import SliderImage1 from "../../Assets/SliderImage1.jpg"
import SliderImage2 from "../../Assets/SliderImage2.jpg"
import SliderImage3 from "../../Assets/SliderImage3.jpg"

// Import social media icons
import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'

// API URL for company info
const COMPANY_INFO_API_URL = "https://raasbackend.vetrinahealthcare.com/company-info/"

// Social media links
const SOCIAL_MEDIA = {
  youtube: "https://www.youtube.com/channel/UCJ3r6VSr5EnxoKEQjYZoWIA",
  facebook: "https://www.facebook.com/vetrinahealthcareenglish/",
  instagram: "https://www.instagram.com/vetrinahealthcareenglish/",
  linkedin: "https://www.linkedin.com/company/14518974",
}

const ProgressStep = ({ steps, currentStep }) => {
  return (
    <div className="progress-container my-4">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="progress-step-container">
            <div
              className={`progress-step ${index <= currentStep ? "active" : ""} ${index < currentStep ? "completed" : ""}`}
            >
              <div className="progress-icon">{index < currentStep ? <i className="fas fa-check"></i> : index + 1}</div>
              <div className="progress-label">{step}</div>
            </div>
          </div>
          {index < steps.length - 1 && <div className={`progress-line ${index < currentStep ? "completed" : ""}`} />}
        </React.Fragment>
      ))}
    </div>
  )
}

const Dashboard = () => {
  const [jobApplications, setJobApplications] = useState([])
  const [currentStep, setCurrentStep] = useState(0)
  const [loadingJobApplications, setLoadingJobApplications] = useState(true)
  const [withdrawingJobId, setWithdrawingJobId] = useState(null)

  // State for company information
  const [companyInfo, setCompanyInfo] = useState({
    what_is_vetrina: "",
    what_is_vetzone: "",
    about_vetrina: "Loading company information...",
    vision: "Loading vision...",
    mission: ["Loading mission...", ""],
    why_work_with_vetrina: "Loading reasons to work with us...",
  })
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(true)
  const [companyInfoError, setCompanyInfoError] = useState(null)

  const steps = ["Profile Setup", "Personality Quiz", "Document verification"]

  useEffect(() => {
    fetchApplicantData()
    fetchDataForProgress()
    fetchCompanyInformation()
  }, [])

  const fetchCompanyInformation = async () => {
    setLoadingCompanyInfo(true)
    setCompanyInfoError(null)
    try {
      const response = await fetch(COMPANY_INFO_API_URL)
      if (response.ok) {
        const data = await response.json()
        // Ensure mission is an array and has at least two elements for display consistency
        const missionData = Array.isArray(data.mission) ? data.mission : []
        while (missionData.length < 2) {
          missionData.push("")
        }

        setCompanyInfo({
          what_is_vetrina: data.what_is_vetrina || "Not available",
          what_is_vetzone: data.what_is_vetzone || "Not available",
          about_vetrina: data.about_vetrina || "Information about Vetrina is not yet available.",
          vision: data.vision || "Our vision is currently being defined.",
          mission: missionData,
          why_work_with_vetrina: data.why_work_with_vetrina || "Discover the benefits of working with us soon.",
        })
      } else {
        console.error("Failed to fetch company information:", response.status)
        setCompanyInfoError(`Failed to load company details (Status: ${response.status}). Displaying defaults.`)
      }
    } catch (error) {
      console.error("Error fetching company information:", error)
      setCompanyInfoError("Could not connect to fetch company details. Please try again later.")
    } finally {
      setLoadingCompanyInfo(false)
    }
  }

  const fetchApplicantData = () => {
    const userId = localStorage.getItem("user_id")
    if (!userId) {
      console.error("User ID not found in local storage")
      setLoadingJobApplications(false)
      return
    }
    setLoadingJobApplications(true)
    fetch(`https://raasbackend.vetrinahealthcare.com/applicants/${userId}/job-applications/`)
      .then((response) => (response.ok ? response.json() : Promise.reject(`HTTP error! status: ${response.status}`)))
      .then((data) => setJobApplications(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error("Error fetching applicant data:", error)
        setJobApplications([])
      })
      .finally(() => setLoadingJobApplications(false))
  }

  const fetchDataForProgress = () => {
    const userId = localStorage.getItem("user_id")
    const email = localStorage.getItem("email")
    if (!userId || !email) {
      console.error("User ID or email not found")
      return
    }
    fetch(`https://raasbackend.vetrinahealthcare.com/api/dashboard/${userId}/status/?email=${encodeURIComponent(email)}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(`HTTP error! status: ${response.status}`)))
      .then((data) => updateCurrentStep(data))
      .catch((error) => console.error("Error fetching dashboard status:", error))
  }

  const updateCurrentStep = (data) => {
    let step = 0
    if (data.Resume_Filled) step = 1
    if (data.Resume_Filled && data.QuizTaken) step = 2
    if (data.Resume_Filled && data.QuizTaken && data.AllStatusesY) step = 3
    setCurrentStep(step)
  }

  const formatJobStatus = (status) => {
    if (!status) return "N/A"
    const formatted = status.toLowerCase().replace(/_/g, " ")
    if (formatted === "under review") return "Under Review"
    if (formatted === "interview scheduled") return "Interview Scheduled"
    return formatted.charAt(0).toUpperCase() + formatted.slice(1)
  }

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) return "N/A"
    const options = { year: "numeric", month: "short", day: "numeric" }
    try {
      return new Date(dateString).toLocaleDateString(undefined, options)
    } catch (error) {
      console.error("Error formatting date:", dateString, error)
      return "Invalid Date"
    }
  }

  const handleWithdraw = (jobApplicationId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, withdraw it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setWithdrawingJobId(jobApplicationId)
        fetch(`https://raasbackend.vetrinahealthcare.com/job-applications/${jobApplicationId}/withdraw/`, { method: "DELETE" })
          .then((response) => {
            if (response.ok) {
              Swal.fire("Withdrawn!", "Your application has been withdrawn.", "success")
              setJobApplications((prev) => prev.filter((jobApp) => jobApp.id !== jobApplicationId))
            } else {
              return response
                .json()
                .then((err) => Promise.reject(err))
                .catch(() => Promise.reject({ detail: `Status: ${response.status}` }))
            }
          })
          .catch((error) => {
            console.error("Failed to withdraw job application:", error)
            Swal.fire("Error!", `Failed to withdraw application. ${error.detail || "Please try again."}`, "error")
          })
          .finally(() => setWithdrawingJobId(null))
      }
    })
  }

  // Helper to render text with line breaks
  const renderTextWithLineBreaks = (text) => {
    if (!text) return null
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
  }

  return (
    <>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="col-lg-10 col-md-8 col-sm-12 dash4 main">
          {" "}
          {/* Removed paddingTop style here */}
          {/* Hero Carousel Section with Social Media Links */}
          <div className="hero-carousel-section">
            <div className="social-media-links">
              <a href={SOCIAL_MEDIA.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <Youtube size={20} />
              </a>
              <a href={SOCIAL_MEDIA.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href={SOCIAL_MEDIA.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href={SOCIAL_MEDIA.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
            <Carousel fade interval={3000} indicators={true} controls={true}>
              <Carousel.Item>
                <img className="d-block w-100" src={SliderImage1 || "/placeholder.svg"} alt="Franchise Opportunity" />
               
              </Carousel.Item>
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src={SliderImage2 || "/placeholder.svg"}
                  alt="Let's Connect & Grow Together"
                />
                
              </Carousel.Item>
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src={SliderImage3 || "/placeholder.svg"}
                  alt="Learn, Experience & Explore"
                />
               
              </Carousel.Item>
            </Carousel>
          </div>
          {/* Video Section - What is Vetrina? */}
          <section className="video-section why-vetrina-section">
            <div className="container">
              <div className="section-header text-center">
                <h2 className="section-title">
                  Why <span className="text-highlight">Vetrina</span>
                </h2>
                <p className="section-subtitle">
                  {loadingCompanyInfo ? (
                    <Spinner animation="grow" size="sm" />
                  ) : (
                    renderTextWithLineBreaks(companyInfo.what_is_vetrina)
                  )}
                </p>
                <div className="section-divider"></div>
              </div>
              <div className="video-container">
                <video autoPlay loop controls muted playsInline> {/* Added autoPlay and loop */}
                  <source src={WhyVetrinaVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </section>
          {/* Video Section - What is VetZone */}
          <section className="video-section vetzone-section">
            <div className="container">
              <div className="section-header text-center">
                <h2 className="section-title">
                  Why <span className="text-highlight">VetZone</span>
                </h2>
                <p className="section-subtitle">
                  {loadingCompanyInfo ? (
                    <Spinner animation="grow" size="sm" />
                  ) : (
                    renderTextWithLineBreaks(companyInfo.what_is_vetzone)
                  )}
                </p>
                <div className="section-divider"></div>
              </div>
              <div className="video-container">
                <video autoPlay loop controls muted playsInline> {/* Added autoPlay and loop */}
                  <source src={VetZoneVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </section>
          {/* Dashboard Content Area */}
          <div className="dashboard-content">
            {/* About Vetrina Section */}
            <section className="about-section">
              <div className="container">
                <div className="section-header text-center">
                  <h2 className="section-title">
                    About <span className="text-highlight">Vetrina</span>
                  </h2>
                  <div className="section-divider"></div>
                </div>
                <div className="row">
                  <div className="col-lg-12">
                    <p className="about-text">
                      {loadingCompanyInfo && !companyInfo.about_vetrina.startsWith("Loading") ? (
                        <Spinner animation="grow" size="sm" />
                      ) : (
                        renderTextWithLineBreaks(companyInfo.about_vetrina)
                      )}
                      {companyInfoError && <span className="text-danger ms-2">{companyInfoError}</span>}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Vision and Mission Section */}
            <section className="vision-mission-side-section">
              <div className="container">
                <div className="vm-container">
                  {/* Mission Section - MOVED FIRST */}
                  <div className="row align-items-center vm-row">
                    <div className="col-md-6 order-md-1 vm-image-wrapper">
                      <img src={MissionImage || "/placeholder.svg"} alt="Mission" className="img-fluid vm-image" />
                    </div>
                    <div className="col-md-6 order-md-2 vm-text-content">
                      <h2 className="vm-side-title">Mission</h2>
                      {loadingCompanyInfo && !companyInfo.mission[0]?.startsWith("Loading") ? (
                        <Spinner animation="grow" size="sm" />
                      ) : (
                        companyInfo.mission.map(
                          (missionPoint, index) =>
                            missionPoint && (
                              <p key={index} className="vm-side-text">
                                {renderTextWithLineBreaks(missionPoint)}
                              </p>
                            ),
                        )
                      )}
                      {companyInfoError && <span className="text-danger ms-2 d-block">{companyInfoError}</span>}
                    </div>
                  </div>
                  <div className="vm-divider"></div>
                  {/* Vision Section - MOVED SECOND */}
                  <div className="row align-items-center vm-row">
                    <div className="col-md-6 vm-text-content">
                      <h2 className="vm-side-title">Vision</h2>
                      <p className="vm-side-text">
                        {loadingCompanyInfo && !companyInfo.vision.startsWith("Loading") ? (
                          <Spinner animation="grow" size="sm" />
                        ) : (
                          renderTextWithLineBreaks(companyInfo.vision)
                        )}
                        {companyInfoError && <span className="text-danger ms-2 d-block">{companyInfoError}</span>}
                      </p>
                    </div>
                    <div className="col-md-6 vm-image-wrapper">
                      <img src={VisionImage || "/placeholder.svg"} alt="Vision" className="img-fluid vm-image" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Why Work With Vetrina Section */}
            <section className="why-work-section">
              <div className="container">
                <div className="section-header text-center">
                  <h2 className="section-title">
                    Why work with <span className="text-highlight">Vetrina?</span>
                  </h2>
                  <div className="section-divider"></div>
                </div>
                <div className="row">
                  <div className="col-lg-12">
                    <p className="why-work-text">
                      {loadingCompanyInfo && !companyInfo.why_work_with_vetrina.startsWith("Loading") ? (
                        <Spinner animation="grow" size="sm" />
                      ) : (
                        renderTextWithLineBreaks(companyInfo.why_work_with_vetrina)
                      )}
                      {companyInfoError && <span className="text-danger ms-2">{companyInfoError}</span>}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Employee Benefits Section */}
            <section className="benefits-section">
              <div className="container">
                <div className="section-header text-center">
                  <h2 className="section-title">
                    Employee <span className="text-highlight">Benefits</span>
                  </h2>
                  <div className="section-divider"></div>
                </div>
                <div className="row justify-content-center">
                  <div className="col-lg col-md-4 col-sm-6 col-12 mb-4 d-flex">
                    <div className="benefit-card w-100">
                      <div className="benefit-icon">
                        <img src={ServiceIcon || "/placeholder.svg"} alt="Skill Development" />
                      </div>
                      <h4 className="benefit-title">Skill & Competency Development</h4>
                    </div>
                  </div>
                  <div className="col-lg col-md-4 col-sm-6 col-12 mb-4 d-flex">
                    <div className="benefit-card w-100">
                      <div className="benefit-icon">
                        <i className="fas fa-shield-alt"></i>
                      </div>
                      <h4 className="benefit-title">Safety & Security</h4>
                    </div>
                  </div>
                  <div className="col-lg col-md-4 col-sm-6 col-12 mb-4 d-flex">
                    <div className="benefit-card w-100">
                      <div className="benefit-icon">
                        <img src={ProductIcon || "/placeholder.svg"} alt="Rewards & Recognition" />
                      </div>
                      <h4 className="benefit-title">Rewards & Recognition</h4>
                    </div>
                  </div>
                  <div className="col-lg col-md-4 col-sm-6 col-12 mb-4 d-flex">
                    <div className="benefit-card w-100">
                      <div className="benefit-icon">
                        <img src={PeopleIcon || "/placeholder.svg"} alt="Work Life Balance" />
                      </div>
                      <h4 className="benefit-title">Work Life Balance</h4>
                    </div>
                  </div>
                  <div className="col-lg col-md-4 col-sm-6 col-12 mb-4 d-flex">
                    <div className="benefit-card w-100">
                      <div className="benefit-icon">
                        <img src={CustomerIcon || "/placeholder.svg"} alt="Health & Wellness" />
                      </div>
                      <h4 className="benefit-title">Health & Wellness</h4>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* User Progress Section */}
            <section className="user-progress-section">
              <div className="container">
                <div className="section-header">
                  <h2 className="section-title text-center">
                    Your <span className="text-highlight">Progress</span>
                  </h2>
                  <div className="section-divider"></div>
                </div>
                <div className="progress-tracker mb-4">
                  <ProgressStep steps={steps} currentStep={currentStep} />
                </div>
                <div className="action-buttons d-flex gap-3 mb-4 mt-4">
                  <Link to="/jobapplication" className="btn btn-primary flex-fill">
                    <i className="fas fa-user-edit me-2"></i>Update Profile
                  </Link>
                  <Link to="/Jobpostingresult" className="btn btn-secondary flex-fill">
                    <i className="fas fa-search me-2"></i>View Opportunities
                  </Link>
                </div>
              </div>
            </section>

            {/* Job Applications Section */}
            <section className="job-applications-section">
              <div className="container">
                <div className="section-header">
                  <h2 className="section-title text-center">
                    Your <span className="text-highlight">Applications</span>
                  </h2>
                  <div className="section-divider"></div>
                </div>
                <div className="applications-container">
                  {loadingJobApplications ? (
                    <div className="text-center my-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Fetching your job applications...</p>
                    </div>
                  ) : jobApplications.length === 0 ? (
                    <div className="empty-state text-center my-5">
                      <div className="empty-state-icon mb-3">
                        <i className="fas fa-file-alt fa-4x text-light-purple"></i>
                      </div>
                      <h4 className="mt-3">No Applications Found</h4>
                      <p className="text-muted">
                        You haven't applied for any jobs yet. Let's find your next opportunity!
                      </p>
                      <Link to="/Jobpostingresult" className="btn btn-outline-primary mt-3">
                        <i className="fas fa-search me-2"></i> Browse Job Openings
                      </Link>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover application-table align-middle">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Job Title</th>
                            <th scope="col">Status</th>
                            <th scope="col">Applied / Updated On</th>
                            <th scope="col" className="text-center">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobApplications.map((jobApp) => (
                            <tr key={jobApp.id}>
                              <td data-label="Job Title">{jobApp.job_title || "N/A"}</td>
                              <td data-label="Status">
                                <span
                                  className={`status-badge status-${jobApp.job_status?.toLowerCase().replace(/\s+/g, "_") || "unknown"}`}
                                >
                                  {formatJobStatus(jobApp.job_status)}
                                </span>
                              </td>
                              <td data-label="Applied/Updated On">
                                {formatDate(jobApp.status_update_time || jobApp.applied_date)}
                              </td>
                              <td data-label="Actions" className="text-center">
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleWithdraw(jobApp.id)}
                                  disabled={withdrawingJobId === jobApp.id}
                                  title="Withdraw Application"
                                >
                                  {withdrawingJobId === jobApp.id ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Withdrawing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-times-circle me-1"></i> Withdraw
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>{" "}
          {/* End dashboard-content */}
        </div>{" "}
        {/* End main col */}
      </div>{" "}
      {/* End wrapper */}
      {/* CSS */}
      <style jsx>{`
        /* Custom CSS */
        :root {
           --primary-color: #8e44ad;
           --secondary-color: #3498db;
           --highlight-color: #9b59b6;
           --text-dark: #333;
           --text-light: #555;
           --text-muted: #6c757d;
           --background-light: #f9f9f9;
           --background-grey: #f5f5f5;
           --white: #ffffff;
           --border-color: #eaeaea;
           --vm-border-color: #8e44ad;
           --vm-image-border-radius: 15px;
           --benefit-icon-size: 60px;
           --success-bg: #e8f5e9;
           --success-text: #388e3c;
           --warning-bg: #fff8e1;
           --warning-text: #ff8f00;
           --info-bg: #e3f2fd;
           --info-text: #1976d2;
           --danger-bg: #ffebee;
           --danger-text: #d32f2f;
           --unknown-bg: #f5f5f5;
           --unknown-text: #666;
           --carousel-item-bg: #000000;
           --gradient-start: #B8529E;
           --gradient-end: #f4811ee5;
        }

        .main { background-color: var(--background-light); font-family: 'Poppins', sans-serif; }
        
        /* Hero Carousel Section with Social Media Links */
        .hero-carousel-section { 
          margin-bottom: 0; 
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); 
          position: relative; 
          z-index: 1; 
        }

        .social-media-links {
          position: absolute;
          top: 15px;
          right: 15px;
          z-index: 10;
          display: flex;
          gap: 15px;
          background-color: rgba(0, 0, 0, 0.5);
          padding: 8px 15px;
          border-radius: 30px;
        }

        .social-media-links a {
          color: white;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .social-media-links a:hover {
          transform: scale(1.2);
          color: var(--primary-color);
        }

        .hero-carousel-section .carousel-item {
          height: 500px; 
          background-color: var(--carousel-item-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .hero-carousel-section .carousel-item img {
          object-fit: fill !important; 
          width: 100%;
          height: 100%;
        }

        .hero-carousel-section .carousel-caption {
          background-color: rgba(0, 0, 0, 0.6);
          padding: 15px;
          border-radius: 8px;
          max-width: 80%;
          margin: 0 auto;
        }
        
        .video-section { padding: 60px 0; background-color: var(--white); border-bottom: 1px solid var(--border-color); }
        .video-section .section-subtitle { 
            font-size: 1.1rem;
            color: var(--text-light);
            margin-top: -10px;
            margin-bottom: 20px;
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;
            line-height: 1.6;
            text-align: justify; /* MODIFIED: Added text-align: justify */
        }
        .video-container { 
          max-width: 900px; 
          margin: 0 auto; 
          background-color: #000; 
          border-radius: 12px;
          overflow: hidden; 
          position: relative;
          box-shadow: 0 0 35px rgba(45, 42, 44, 0.19), 0 0 35px rgba(73, 70, 67, 0.9);
        }
        
        .video-container::before {
          content: '';
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          background: linear-gradient(135deg,rgb(219, 90, 15),rgba(233, 112, 6, 0.9));
          border-radius: 20px;
          z-index: -1;
          filter: blur(20px);
          opacity: 0.9;
        }
        
        .video-section video { 
          display: block; 
          width: 100%; 
          height: auto; 
          border-radius: 8px; 
        }
        
        .dashboard-content section { padding: 60px 0; border-bottom: 1px solid var(--border-color); }
        .dashboard-content section:last-child { border-bottom: none; }
        .section-header { margin-bottom: 40px; }
        .section-title { font-size: 2.2rem; font-weight: 700; text-align: center; margin-bottom: 15px; color: var(--text-dark); }
        .section-divider { height: 4px; width: 70px; background: linear-gradient(to right, #B8529E, #f4811ee5); margin: 0 auto 30px; border-radius: 2px; }
        .text-highlight { color: var(--primary-color); font-weight: 700; }
        .about-section { background-color: var(--white); }
        .about-text { font-size: 1.05rem; line-height: 1.7; color: var(--text-light); text-align: justify; margin-bottom: 1rem;} /* ALREADY JUSTIFIED */

        .vision-mission-side-section { 
          background-color: var(--white); 
          padding: 40px 0;
        }
        
        .vm-container {
          background-color: var(--white);
          border-radius: 16px;
          padding: 25px 20px;
          position: relative;
          box-shadow: 0 10px 40px rgba(130, 13, 169, 0.15);
          overflow: hidden;
          max-width: 95%;
          margin: 0 auto;
        }
        
        .vm-container::before {
          content: '';
          position: absolute;
          top: -15px;
          left: -15px;
          right: -15px;
          bottom: -15px;
          background: linear-gradient(135deg, #B8529E, #f4811ee5);
          border-radius: 25px;
          z-index: -1;
          filter: blur(25px);
          opacity: 0.85;
        }
        
        .vm-text-content { 
          display: flex; 
          flex-direction: column; 
          justify-content: center; 
          padding: 15px; 
          height: 100%; 
          text-align: center; 
        }
        
        .vm-side-title { 
          font-size: 2rem; 
          font-weight: 700; 
          color: var(--text-dark); 
          margin-bottom: 15px; 
          position: relative;
          display: inline-block;
        }
        
        .vm-side-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 3px;
          background: linear-gradient(to right, #B8529E, #f4811ee5);
          border-radius: 3px;
        }
        
        .vm-side-text { 
          font-size: 0.95rem; 
          line-height: 1.6; 
          color: var(--text-light); 
          margin-bottom: 0.5rem; 
          /* text-align: justify; // Individual mission/vision points often look better left-aligned or centered if short */
        }
        
        .vm-image-wrapper { 
          padding: 10px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
        }
        
        .vm-image { 
          display: block; 
          max-width: 100%; 
          height: auto; 
          border-radius: var(--vm-image-border-radius); 
          box-shadow: 0 10px 25px rgba(45, 42, 44, 0.19), 0 10px 25px rgba(73, 70, 67, 0.9); 
          max-height: 280px; 
          object-fit: cover; 
          width: 100%; 
        }
        
        .vm-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, #B8529E, #f4811ee5, transparent);
          margin: 20px 0;
          opacity: 0.5;
        }
        
        .vm-row {
          margin-bottom: 0 !important;
        }

        .why-work-section { background-color: var(--white); }
        .why-work-text { font-size: 1.05rem; line-height: 1.7; color: var(--text-light); margin-bottom: 1rem; text-align: justify; } /* ALREADY JUSTIFIED */

        .benefits-section { background-color: var(--background-grey); }
        .benefit-card { 
          display: flex; 
          flex-direction: column; 
          justify-content: center; 
          align-items: center; 
          background-color: var(--white); 
          border-radius: 10px; 
          padding: 30px 15px; 
          text-align: center; 
          height: 100%; 
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05); 
          transition: transform 0.3s ease, box-shadow 0.3s ease; 
          position: relative;
          overflow: hidden;
        }
        
        .benefit-card:hover { 
          transform: translateY(-8px); 
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1); 
        }
        
        .benefit-card:hover::before {
          opacity: 0.7;
        }
        
        .benefit-card::before {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          background: linear-gradient(135deg, #B8529E, #f4811ee5);
          border-radius: 15px;
          z-index: -1;
          filter: blur(10px);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .benefit-icon {
           margin-bottom: 20px;
           line-height: 1;
           height: var(--benefit-icon-size);
           width: var(--benefit-icon-size);
           display: flex;
           align-items: center;
           justify-content: center;
        }
        .benefit-icon img {
            max-width: 100%;
            max-height: 100%;
            height: auto;
            object-fit: contain;
        }
        .benefit-icon i {
            font-size: 2.5rem;
            color: #a2228e;
        }

        .benefit-title { font-size: 1rem; font-weight: 600; color: var(--text-dark); margin-top: auto; }

        .user-progress-section { background-color: var(--white); }
        .progress-container { display: flex; justify-content: space-between; align-items: flex-start; margin: 30px 0; position: relative; padding: 0 15px; }
        .progress-line { flex-grow: 1; height: 4px; background-color: #e9ecef; margin: 0 -2px; transform: translateY(23px); transition: background-color 0.4s ease; z-index: 0; }
        .progress-line.completed { background-color: var(--primary-color); }
        .progress-step-container { display: flex; flex-direction: column; align-items: center; text-align: center; width: 100px; position: relative; z-index: 1; }
        .progress-icon { width: 50px; height: 50px; border-radius: 50%; background-color: #e9ecef; color: var(--text-muted); display: flex; justify-content: center; align-items: center; margin-bottom: 10px; font-weight: bold; font-size: 1.2rem; transition: all 0.4s ease; border: 3px solid #e9ecef; position: relative; z-index: 2; }
        .progress-step.active .progress-icon { border-color: var(--primary-color); background-color: var(--white); color: var(--primary-color); }
        .progress-step.completed .progress-icon { background-color: var(--primary-color); color: var(--white); border-color: var(--primary-color); }
        .progress-label { font-size: 0.9rem; color: var(--text-muted); font-weight: 500; transition: color 0.4s ease; max-width: 90px; line-height: 1.3; }
        .progress-step.active .progress-label { color: var(--primary-color); font-weight: 600; }
        .action-buttons { margin-top: 40px; }
        
        .btn-primary, .btn-secondary { 
          position: relative;
          border: none;
          padding: 12px 25px; 
          font-weight: 600; 
          transition: all 0.3s ease; 
          background-color: #fff;
          color: var(--text-dark);
          overflow: hidden;
          z-index: 1;
          border-radius: 6px;
        }
        
        .btn-primary::before, .btn-secondary::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, #B8529E, #f4811ee5);
          z-index: -1;
        }
        
        .btn-secondary::before {
          background: linear-gradient(45deg, #f4811ee5, #B8529E);
        }
        
        .btn-primary::after, .btn-secondary::after {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          background: linear-gradient(45deg, #B8529E, #f4811ee5);
          z-index: -2;
          filter: blur(10px);
          opacity: 0.6;
          border-radius: 10px;
        }
        
        .btn-secondary::after {
          background: linear-gradient(45deg, #f4811ee5, #B8529E);
        }
        
        .btn-primary:hover, .btn-primary:focus,
        .btn-secondary:hover, .btn-secondary:focus { 
          transform: translateY(-3px); 
          color: white;
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2); 
        }
        
        .btn-block { display: block; width: 100%; }

        .job-applications-section { background-color: var(--white); }
        .applications-container { 
          background-color: var(--white); 
          border-radius: 10px; 
          padding: 20px; 
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05); 
          margin-top: 20px; 
          position: relative;
          overflow: hidden;
        }
        
        .applications-container::before {
          content: '';
          position: absolute;
          top: -8px;
          left: -8px;
          right: -8px;
          bottom: -8px;
          background: linear-gradient(135deg, #B8529E, #f4811ee5);
          border-radius: 18px;
          z-index: -1;
          filter: blur(15px);
          opacity: 0.4;
        }
        
        .application-table { margin-bottom: 0; }
        .application-table th { color: var(--text-dark); font-weight: 600; border-bottom: 2px solid #dee2e6; white-space: nowrap; }
        .application-table td { vertical-align: middle; padding: 12px 10px; }
        .status-badge { padding: 6px 14px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .status-applied { background-color: var(--info-bg); color: var(--info-text); }
        .status-under_review { background-color: var(--warning-bg); color: var(--warning-text); }
        .status-interview_scheduled, .status-selected { background-color: var(--success-bg); color: var(--success-text); }
        .status-rejected { background-color: var(--danger-bg); color: var(--danger-text); }
        .status-unknown, .status- { background-color: var(--unknown-bg); color: var(--unknown-text); }
        .empty-state { padding: 60px 20px; text-align: center; background-color: var(--background-grey); border-radius: 8px; }
        .empty-state-icon { margin-bottom: 20px; }
        .text-light-purple { color: #c3aed6; }
        
        .btn-outline-primary { 
          position: relative;
          background: transparent;
          color: var(--text-dark); 
          border: none;
          transition: all 0.3s ease; 
          font-weight: 600; 
          padding: 8px 20px; 
          z-index: 1;
          overflow: hidden;
        }
        
        .btn-outline-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border: 2px solid transparent;
          background: linear-gradient(45deg, #B8529E, #f4811ee5) border-box;
          -webkit-mask: 
             linear-gradient(#fff 0 0) padding-box, 
             linear-gradient(#fff 0 0);
          -webkit-mask-composite: destination-out;
          mask-composite: exclude;
          border-radius: 6px;
          z-index: -1;
        }
        
        .btn-outline-primary:hover { 
          color: white;
        }
        
        .btn-outline-primary:hover::before {
          background: linear-gradient(45deg, #B8529E, #f4811ee5);
          -webkit-mask: none;
          mask: none;
        }
        
        .btn-danger { background-color: #e74c3c; border-color: #e74c3c; transition: all 0.3s ease; }
        .btn-danger:hover { background-color: #c0392b; border-color: #c0392b; }
        .btn-danger:disabled { background-color: #f5b7b1; border-color: #f5b7b1; }

        /* Responsive Styles */
        @media (max-width: 992px) {
          .hero-carousel-section .carousel-item { height: 400px; } 
          .section-title { font-size: 2rem; }
          .vm-side-title { font-size: 1.8rem; }
          .vm-side-text { font-size: 0.9rem; }
          .vm-text-content { text-align: left; } /* Consider if vision/mission points should remain centered/left on smaller screens */
          .progress-label { font-size: 0.8rem; max-width: 70px; }
          .progress-icon { width: 45px; height: 45px; font-size: 1.1rem;}
          .progress-line { transform: translateY(20.5px); }
          .social-media-links { padding: 6px 12px; }
          .social-media-links a { transform: scale(0.9); }
          .vm-container { padding: 20px 15px; }
        }

        @media (max-width: 767.98px) {
          .hero-carousel-section .carousel-item { height: 350px; } 
          .video-section, .dashboard-content section { padding: 40px 0; }
          .vm-row { margin-bottom: 0 !important; }
          .vm-text-content { padding: 15px; text-align: center; /* Re-center for mobile consistency */ } 
          .vm-image-wrapper { padding: 10px; }
          .vm-image { max-height: 200px; }
          
          /* Specific re-ordering for Mission first on mobile */
          .vm-container > .vm-row:first-child .vm-image-wrapper { order: 1; }
          .vm-container > .vm-row:first-child .vm-text-content { order: 2; }
          .vm-container > .vm-row:last-child .vm-text-content { order: 2; } /* Vision text */
          .vm-container > .vm-row:last-child .vm-image-wrapper { order: 1; } /* Vision image */

          .vm-side-title { font-size: 1.6rem; }
          .vm-side-text { font-size: 0.85rem; }
          .benefit-card { padding: 20px 10px; }
          .benefit-title { font-size: 0.9rem; }
          .progress-container { flex-wrap: wrap; justify-content: center; }
          .progress-step-container { width: 33%; margin-bottom: 20px; }
          .progress-line { display: none; }
          /* .action-buttons .col-md-6 { width: 100%; } - REMOVED to keep buttons in single line */
          .application-table th, .application-table td { font-size: 0.9rem; padding: 8px 5px; }
          .status-badge { padding: 5px 10px; font-size: 0.75rem; }
          .social-media-links { top: 10px; right: 10px; gap: 10px; }
          .vm-container { padding: 20px 10px; }
          .vision-mission-side-section { padding: 30px 0; }
        }

        @media (max-width: 576px) {
          .hero-carousel-section .carousel-item { height: 300px; } 
          .section-title { font-size: 1.8rem; }
          .video-container { max-width: 100%; border-radius: 0; }
          .video-section video { border-radius: 0; }
          .vm-side-title { font-size: 1.5rem; margin-bottom: 15px; }
          .vm-side-text { font-size: 0.8rem; line-height: 1.5; }
          .vm-image { max-height: 180px; }
          .benefit-icon { --benefit-icon-size: 50px; }
          .progress-step-container { width: 50%; }
          .progress-icon { width: 40px; height: 40px; font-size: 1rem; }
          .progress-label { font-size: 0.75rem; max-width: 60px; }
          .application-table { display: block; width: 100%; }
          .application-table thead { display: none; }
          .application-table tbody, .application-table tr, .application-table td { display: block; width: 100%; }
          .application-table tr { border: 1px solid #eee; margin-bottom: 15px; padding: 10px; border-radius: 5px; }
          .application-table td { border: none; position: relative; padding-left: 50%; text-align: right; border-bottom: 1px dotted #eee; min-height: 30px;}
          .application-table td:last-child { border-bottom: none; }
          .application-table td::before { content: attr(data-label); position: absolute; left: 10px; width: calc(50% - 20px); padding-right: 10px; white-space: nowrap; text-align: left; font-weight: bold; color: var(--text-dark); font-size: 0.85rem; }
          .application-table td[data-label="Actions"] { text-align: center; padding-left: 0; }
          .application-table td[data-label="Actions"]::before { display: none; }
          .social-media-links { padding: 5px 10px; gap: 8px; }
          .social-media-links a { transform: scale(0.8); }
          .vm-container { padding: 15px 8px; }
          .vision-mission-side-section { padding: 25px 0; }
        }
      `}</style>
    </>
  )
}

export default Dashboard